/**
 * Phase 1.5 — Extraction Accuracy Dashboard
 * ==========================================
 * System → OCR Analytics
 *
 * Primary tool for tracking the 4-condition Phase 2 stability bar:
 *   - % auto-applied vs HR-reviewed
 *   - Per-field confidence histograms
 *   - Average / p50 / p95 / max latency per document
 *   - Tesseract fallback event count (must be 0 for Condition 3)
 *
 * No external chart libraries — pure CSS/SVG to match the rest of the app.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Clock,
  Eye,
  FileSearch,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WINDOWS = [
  { value: '7', label: { en: 'Last 7 days', ar: 'آخر 7 أيام' } },
  { value: '14', label: { en: 'Last 14 days', ar: 'آخر 14 يومًا' } },
  { value: '30', label: { en: 'Last 30 days', ar: 'آخر 30 يومًا' } },
  { value: '0', label: { en: 'All time', ar: 'كل الوقت' } },
];

const BUCKET_LABELS = {
  very_low: '0–50%',
  low: '50–60%',
  medium: '60–70%',
  borderline: '70–85%',
  high: '85–95%',
  very_high: '95–100%',
};

const BUCKET_COLORS = {
  very_low: 'bg-red-500',
  low: 'bg-orange-500',
  medium: 'bg-amber-500',
  borderline: 'bg-yellow-500',
  high: 'bg-lime-500',
  very_high: 'bg-emerald-500',
};

const OCRAnalyticsPage = () => {
  const { token } = useAuth();
  const { isRTL } = useLanguage();
  const [days, setDays] = useState('7');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/system/ocr-analytics/`, {
        params: { days },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAnalytics();
  }, [days, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtPct = (n) => `${(n * 100).toFixed(1)}%`;
  const fmtMs = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`);

  // Stability bar derived flags — match the 4 conditions in PHASE_1_READINESS.md
  const stabilityFlags = data
    ? {
        cond2: {
          ok: data.stability_bar.condition_2_n >= 50
            && data.stability_bar.condition_2_success_rate >= 0.9,
          rate: data.stability_bar.condition_2_success_rate,
          n: data.stability_bar.condition_2_n,
        },
        cond3: {
          ok: data.stability_bar.condition_3_unexpected_fallbacks === 0,
          count: data.stability_bar.condition_3_unexpected_fallbacks,
        },
      }
    : null;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="ocr-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileSearch className="h-6 w-6 text-blue-600" />
            {isRTL ? 'تحليلات استخراج المستندات' : 'OCR Extraction Analytics'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isRTL
              ? 'مؤشرات أداء خط أنابيب قراءة المستندات بالذكاء الاصطناعي.'
              : 'Live KPIs from the Claude vision document-reading pipeline.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-44" data-testid="ocr-analytics-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.value} value={w.value}>
                  {isRTL ? w.label.ar : w.label.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAnalytics}
            disabled={loading}
            data-testid="ocr-analytics-refresh"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-sm text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}

      {!data && loading && (
        <p className="text-sm text-slate-500">{isRTL ? 'جارٍ التحميل...' : 'Loading…'}</p>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
              label={isRTL ? 'نسبة التطبيق التلقائي' : 'Auto-applied rate'}
              value={fmtPct(data.auto_apply.auto_apply_rate)}
              sublabel={`${data.auto_apply.auto_applied_count} ${isRTL ? 'تلقائي' : 'auto'} · ${data.auto_apply.hr_reviewed_count} ${isRTL ? 'بمراجعة' : 'reviewed'}`}
              testid="kpi-auto-apply-rate"
            />
            <KPICard
              icon={<Clock className="h-5 w-5 text-blue-600" />}
              label={isRTL ? 'متوسط زمن المعالجة' : 'Average latency'}
              value={fmtMs(data.latency_ms.average)}
              sublabel={`p95 ${fmtMs(data.latency_ms.p95)} · max ${fmtMs(data.latency_ms.max)}`}
              testid="kpi-latency"
            />
            <KPICard
              icon={<Activity className="h-5 w-5 text-violet-600" />}
              label={isRTL ? 'المستندات المعالجة' : 'Documents processed'}
              value={data.totals.documents_processed.toLocaleString()}
              sublabel={`${data.totals.jobs_failed} ${isRTL ? 'فشل' : 'failed'}`}
              testid="kpi-docs"
            />
            <KPICard
              icon={
                data.tesseract_fallback_events === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                )
              }
              label={isRTL ? 'احتياطي Tesseract' : 'Tesseract fallbacks'}
              value={data.tesseract_fallback_events.toString()}
              sublabel={
                data.tesseract_fallback_events === 0
                  ? isRTL
                    ? 'مسار الرؤية سليم'
                    : 'Vision path healthy'
                  : isRTL
                    ? 'يستحق التحقيق'
                    : 'Investigate'
              }
              tone={data.tesseract_fallback_events === 0 ? 'good' : 'warn'}
              testid="kpi-fallback"
            />
          </div>

          {/* Stability bar — explicit Condition 2 + Condition 3 status */}
          <Card data-testid="stability-bar">
            <CardHeader>
              <CardTitle className="text-base">
                {isRTL ? 'مؤشر الاستقرار للمرحلة 2' : 'Phase 2 Stability Bar'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <StabilityRow
                label={
                  isRTL
                    ? 'الشرط 2: معدّل نجاح ≥ 90% على ≥ 50 مستند'
                    : 'Condition 2: success rate ≥ 90% across ≥ 50 docs'
                }
                ok={stabilityFlags.cond2.ok}
                detail={`${fmtPct(stabilityFlags.cond2.rate)} on ${stabilityFlags.cond2.n} ${isRTL ? 'مستند' : 'docs'}`}
              />
              <StabilityRow
                label={
                  isRTL
                    ? 'الشرط 3: لا توجد أحداث احتياطية غير متوقعة'
                    : 'Condition 3: zero unexpected Tesseract fallbacks'
                }
                ok={stabilityFlags.cond3.ok}
                detail={`${stabilityFlags.cond3.count} ${isRTL ? 'حدث' : 'events'}`}
              />
              <p className="text-xs text-slate-500 pt-2">
                {isRTL
                  ? 'الشرطان 1 و 4 (لا أخطاء P1، نشاط المستخدم) يُتتبَّعان خارج هذه اللوحة.'
                  : 'Conditions 1 and 4 (P1 bugs, HR active usage) are tracked outside this dashboard.'}
              </p>
            </CardContent>
          </Card>

          {/* Provider usage breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {isRTL ? 'استخدام مزوّد OCR' : 'OCR Provider Usage'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProviderBar usage={data.provider_usage} isRTL={isRTL} />
            </CardContent>
          </Card>

          {/* Per-field confidence histograms */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4" />
                {isRTL ? 'مدرّجات الثقة حسب الحقل' : 'Per-Field Confidence Histograms'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.field_confidence_histograms.length === 0 && (
                <p className="text-sm text-slate-500">
                  {isRTL
                    ? 'لا توجد بيانات استخراج في هذه النافذة بعد.'
                    : 'No extraction data in this window yet.'}
                </p>
              )}
              {data.field_confidence_histograms.map((field) => (
                <FieldHistogramRow key={field.field_name} field={field} isRTL={isRTL} />
              ))}
            </CardContent>
          </Card>

          <p className="text-xs text-slate-400 text-right">
            {isRTL ? 'تم التوليد في' : 'Generated at'} {new Date(data.generated_at).toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────── sub-components

const KPICard = ({ icon, label, value, sublabel, tone = 'neutral', testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <div
        className={cn(
          'text-2xl font-semibold',
          tone === 'good' && 'text-emerald-700',
          tone === 'warn' && 'text-amber-700'
        )}
      >
        {value}
      </div>
      <div className="text-xs text-slate-400 mt-1">{sublabel}</div>
    </CardContent>
  </Card>
);

const StabilityRow = ({ label, ok, detail }) => (
  <div
    className={cn(
      'flex items-center justify-between p-3 rounded-md border',
      ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
    )}
  >
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-amber-600" />
      )}
      <span className="text-sm">{label}</span>
    </div>
    <Badge variant={ok ? 'default' : 'secondary'} className={ok ? 'bg-emerald-600' : 'bg-amber-500'}>
      {detail}
    </Badge>
  </div>
);

const ProviderBar = ({ usage, isRTL }) => {
  const total = Object.values(usage).reduce((s, n) => s + n, 0);
  if (total === 0) {
    return (
      <p className="text-sm text-slate-500">
        {isRTL ? 'لا توجد طلبات في هذه النافذة.' : 'No requests in this window.'}
      </p>
    );
  }
  const ORDER = ['claude_vision', 'tesseract', 'aws_textract', 'mock'];
  const COLORS = {
    claude_vision: 'bg-blue-500',
    tesseract: 'bg-amber-500',
    aws_textract: 'bg-slate-400',
    mock: 'bg-slate-300',
  };
  return (
    <div className="space-y-2">
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
        {ORDER.map((k) => {
          const v = usage[k] || 0;
          const pct = (v / total) * 100;
          if (pct === 0) return null;
          return <div key={k} className={COLORS[k]} style={{ width: `${pct}%` }} />;
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        {ORDER.map((k) => {
          const v = usage[k] || 0;
          if (v === 0) return null;
          return (
            <div key={k} className="flex items-center gap-1">
              <span className={cn('inline-block w-2 h-2 rounded-sm', COLORS[k])} />
              <span className="font-mono">{k}</span>
              <span>· {v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FieldHistogramRow = ({ field, isRTL }) => {
  const total = Object.values(field.buckets).reduce((s, n) => s + n, 0);
  return (
    <div className="border rounded-md p-3" data-testid={`field-hist-${field.field_name}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{field.display_name}</span>
          <span className="font-mono text-xs text-slate-400">{field.field_name}</span>
          {field.is_high_risk && (
            <Badge variant="outline" className="border-amber-400 text-amber-700">
              {isRTL ? 'حقل حساس' : 'high-risk'}
            </Badge>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {isRTL ? 'متوسط' : 'avg'} <span className="font-mono">{field.avg_confidence.toFixed(2)}</span> · n={field.n}
        </div>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
        {Object.keys(BUCKET_LABELS).map((k) => {
          const v = field.buckets[k] || 0;
          const pct = total ? (v / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={k}
              className={BUCKET_COLORS[k]}
              style={{ width: `${pct}%` }}
              title={`${BUCKET_LABELS[k]}: ${v}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500 mt-1">
        {Object.keys(BUCKET_LABELS).map((k) => {
          const v = field.buckets[k] || 0;
          if (v === 0) return null;
          return (
            <span key={k} className="flex items-center gap-1">
              <span className={cn('inline-block w-1.5 h-1.5 rounded-sm', BUCKET_COLORS[k])} />
              {BUCKET_LABELS[k]}: {v}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default OCRAnalyticsPage;
